import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles, ChevronDown, ChevronRight, Bot, Monitor, Info } from "lucide-react";

const sections = [
  {
    title: "Your Team Reviews AI Work",
    defaultOpen: true,
    count: 5,
    items: [
      "AI handles claims, EOB posting, payment reconciliation, patient billing, and insurance verification",
      "Your staff reviews and fills in where AI needs help — instead of our human biller",
      "AI handles denial management end-to-end, your team steps in for complex clinical cases",
      "Full transparency into everything AI does via practice manager dashboard",
    ],
  },
  {
    title: "Front Office AI Products",
    defaultOpen: false,
    count: 8,
    items: [
      "Amy (Receptionist)", "Leena (Schedule Optimizer)", "Stephanie (Hygiene Recall)",
      "Elise (Patient Experience)", "Kim (Case Acceptance)", "Morgan (Patient Intake)",
      "Trisha (Practice Manager)", "Joseph (Fax Management)",
    ],
  },
  {
    title: "Communication + Marketing Suite",
    defaultOpen: false,
    count: 12,
    items: [
      "VoIP Phone System", "Physical Desk Phones (Yealink)", "2-Way Texting",
      "Universal Inbox", "Team Chat & Channels", "Smart Voice Logs",
      "Automated Workflows", "Intelligent Fax", "Next-Gen Forms",
      "SMS & Email Campaigns", "Text-to-Pay Payments", "Payment Card Readers",
    ],
  },
  {
    title: "Back Office AI Products",
    defaultOpen: false,
    count: 8,
    items: [
      "Amanda (Verification)", "Ariel (Claim Submission)", "Alayna (Claim Scrubbing)",
      "Sarah (Denial Mgmt)", "Kianna (Patient Billing)", "Julia (AR & Collections)",
      "Alisha (EOB Posting)", "Laura (Insurance Calls)",
    ],
  },
  {
    title: "PMS + Imaging",
    defaultOpen: false,
    count: 12,
    items: [
      "Patient Charts", "Perio Charting", "Ledgers & Billing", "Treatment Planning",
      "Scheduling", "eClaims Built-In", "Cloud-Based — No Server", "Reporting & Analytics",
      "SOTA Cloud Imaging", "AI Copilot", "AI Scribe", "AI Treatment Planner",
    ],
  },
];

const AccordionSection = ({ section, isFirst }) => {
  const [open, setOpen] = useState(section.defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
          open ? "bg-blue-50/60" : "hover:bg-slate-50"
        } ${isFirst ? "rounded-t-xl" : ""}`}
      >
        <div className="flex items-center gap-2.5">
          {open ? (
            <ChevronDown className="w-4 h-4 text-blue-500 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
          )}
          <span className={`text-sm font-semibold ${open ? "text-blue-700" : "text-slate-700"}`}>
            {section.title}
          </span>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
          open ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-400"
        }`}>
          {section.count}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 bg-blue-50/30">
          <div className={`${section.items.length > 5 ? "grid grid-cols-2 gap-x-4 gap-y-1.5" : "space-y-1.5"}`}>
            {section.items.map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-[13px] text-slate-600">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const FlexiblePlanSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blurs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/40 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-60" />

      <div className="w-full flex flex-col h-full relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="mb-5 flex-shrink-0"
        >
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold mb-2 border border-blue-100">
            <Sparkles className="w-3 h-3" />
            Option 2
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Flexible Plan
          </h2>
          <p className="text-base font-medium text-slate-700 leading-relaxed mt-2 max-w-3xl">
            Two distinct components: a per-patient fee for AI doing the work your staff does, and the software platform that consolidates 10+ tools — given free for 2 years.
          </p>
        </motion.div>

        {/* Two columns */}
        <div className="flex gap-5 flex-1 min-h-0">
          {/* Left: What's included accordion */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex-1 bg-white rounded-2xl border border-blue-200 shadow-sm flex flex-col overflow-hidden min-h-0"
          >
            <div className="px-5 pt-4 pb-3 border-b border-blue-100 flex-shrink-0">
              <p className="text-xs font-bold text-blue-500 uppercase tracking-wider">
                What's included
              </p>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {sections.map((section, i) => (
                <AccordionSection key={i} section={section} isFirst={i === 0} />
              ))}
            </div>
          </motion.div>

          {/* Right: Two pricing components stacked */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="w-[400px] flex flex-col gap-3"
          >
            {/* Component 1: AI doing human work */}
            <div className="bg-[#0f172a] rounded-2xl shadow-xl p-5 flex flex-col text-white flex-1">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 ring-1 ring-blue-400/30 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs font-bold text-blue-400 uppercase tracking-wider">
                    Component 1 — AI Labor
                  </p>
                  <p className="text-[11px] text-slate-400">
                    AI replacing $120+ of human work per patient
                  </p>
                </div>
              </div>

              <div className="space-y-2.5 flex-1">
                {/* Startup */}
                <div className="bg-white/5 rounded-xl ring-1 ring-white/10 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-base font-bold text-white">Startup</span>
                      <p className="text-[10px] text-slate-400">150-300 patients</p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-extrabold text-white tracking-tight">$15</span>
                      <span className="text-[10px] text-slate-400 ml-1">/patient</span>
                    </div>
                  </div>
                </div>

                {/* Growth */}
                <div className="bg-blue-500/10 rounded-xl ring-1 ring-blue-400/30 px-4 py-3 relative">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-500 text-white text-[8px] font-bold px-2.5 py-0.5 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-base font-bold text-white">Growth</span>
                      <p className="text-[10px] text-slate-400">300-500 patients</p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-extrabold text-white tracking-tight">$12</span>
                      <span className="text-[10px] text-slate-400 ml-1">/patient</span>
                    </div>
                  </div>
                </div>

                {/* Pro */}
                <div className="bg-white/5 rounded-xl ring-1 ring-white/10 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-base font-bold text-white">Pro</span>
                      <p className="text-[10px] text-slate-400">500-800 patients</p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-extrabold text-white tracking-tight">$8</span>
                      <span className="text-[10px] text-slate-400 ml-1">/patient</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-slate-500 mt-3 pt-3 border-t border-white/10 leading-relaxed">
                This fee covers AI doing the work humans do — answering calls, verifying insurance, submitting claims, posting payments, billing patients, and more.
              </p>
            </div>

            {/* Component 2: Software Platform */}
            <div className="bg-white rounded-2xl border border-blue-200 shadow-sm p-5">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-8 h-8 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <Monitor className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-blue-500 uppercase tracking-wider">
                    Component 2 — Software Platform
                  </p>
                  <p className="text-[11px] text-slate-400">
                    Consolidates 10+ softwares into 1 platform
                  </p>
                </div>
              </div>

              <div className="flex items-baseline gap-2 mb-1.5">
                <span className="text-xl font-extrabold text-slate-900">$799</span>
                <span className="text-xs text-slate-400">/mo</span>
                <span className="text-xs text-slate-300 mx-1">instead of</span>
                <span className="text-sm font-bold text-red-400 line-through">~$3,700/mo</span>
              </div>

              <div className="bg-blue-50 rounded-lg border border-blue-100 px-3 py-2">
                <p className="text-sm font-bold text-blue-700">
                  Free for the first 2 years on this plan
                </p>
                <p className="text-[11px] text-blue-500">
                  PMS, imaging, comms, marketing, eClaims, payments, verification — all included
                </p>
              </div>
            </div>

            {/* Advisory note */}
            <div className="flex items-start gap-2 px-2">
              <Info className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-[10px] text-slate-400 leading-relaxed">
                AI infrastructure costs can be significant. We may advise on your usage patterns if costs begin exceeding sustainable levels for your contract.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default FlexiblePlanSlide;
